import { Suspense, useEffect, useRef, useState } from "react";
import {
	BrowserRouter,
	HashRouter,
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

import Info from "../Info";
import useCheckQrSession from "../apis/useCheckQrSession";
import { QR_SESSION_STATUS } from "../apis/useCheckQrSession/types";
import { publicBaseUrl } from "../common/envconst";
import { isLinkPayAuthReturning } from "../common/LinkPayAuth/isLinkPayAuthReturning";
import ConditionDelayClose from "../components/Conditional/ConditionDelayClose";
import ConditionTheme from "../components/Conditional/ConditionTheme";
import Layout from "../components/Layout/Layout";
import PageTransition from "../components/PageTransition/PageTransition";
import SplashScreen from "../components/SplashScreen/SplashScreen";
import ToastContainer from "../components/ToastContainer";
import { useConfigContext } from "../contexts/ConfigContext/ConfigContext";
import CartPage from "../pages/CartPage/CartPage";
import ErrorBoundaryPage from "../pages/ErrorBoundaryPage/ErrorBoundaryPage";
import KitchenOrderSentPage from "../pages/KitchenOrderSentPage/KitchenOrderSentPage";
import OrderConfirmation from "../pages/OrderConfirmation/OrderConfirmation";
import PaymentPage from "../pages/PaymentPage/PaymentPage";
import ProductDetailPage from "../pages/ProductDetailPage/ProductDetailPage";
import ProductListPage from "../pages/ProductListPage/ProductListPage";
import QrSessionExpiredPage from "../pages/QrSessionExpiredPage/QrSessionExpiredPage";
import SendToKitchenConfirmPage from "../pages/SendToKitchenConfirmPage/SendToKitchenConfirmPage";
import ShareCartPage from "../pages/ShareCartPage/ShareCartPage";
import "./App.scss";
import { useAppInit } from "./hooks";
import { PATH } from "./path";

// Multi-stall paths always use BrowserRouter (supported in all environments)
// This includes preview paths like "/preview-path/wang/" or "/preview-path/pdd/"
// Non-multi-stall preview builds use HashRouter for compatibility
const isMultiStallPath =
	publicBaseUrl.includes("/wang") ||
	publicBaseUrl.includes("/pdd") ||
	publicBaseUrl.includes("/kokoro") ||
	publicBaseUrl.includes("/torikizoku") ||
	publicBaseUrl.includes("/shabudays");
const isRootPath = publicBaseUrl === "/";
const FinalRouter = isRootPath || isMultiStallPath ? BrowserRouter : HashRouter;

const SplashScreenWrapper = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [minDurationElapsed, setMinDurationElapsed] = useState(false);
	const {
		state: { inited },
	} = useConfigContext((state) => state);
	const { state: stallMode } = useConfigContext((state) => state.stallMode);

	useEffect(() => {
		const timer = setTimeout(() => {
			setMinDurationElapsed(true);
		}, 3000);

		return () => clearTimeout(timer);
	}, []);

	// Splash stays visible until BOTH the min 3s duration has elapsed AND /v1/location
	// (useGetConfig) has resolved (inited=true). Otherwise a slow config fetch leaves a
	// blank product list flashing before the data lands.
	const isSplashVisible = !minDurationElapsed || !inited;

	const handleAnimationEnd = () => {
		if (minDurationElapsed && inited) {
			const from = location.state?.from ?? PATH.PRODUCT_LIST;

			navigate(from, { replace: true });
		}
	};

	return (
		<SplashScreen
			stallMode={stallMode}
			isVisible={isSplashVisible}
			onAnimationEnd={handleAnimationEnd}
		/>
	);
};

function AppRoutes() {
	const { qrsId, locationId: appLocationId } = useAppInit();
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const { state: locationId } = useConfigContext((state) => state.location.id);
	const { state: inited } = useConfigContext((state) => state.inited);
	// Cover stays up until BOTH the locationId is resolved AND useGetConfig (/v1/location)
	// has finished applying its config/theme. Otherwise refreshing on a non-splash route
	// (CartPage, PaymentPage, etc.) flashes default-themed content before the brand theme lands.
	const showLoadingCover = !locationId || !inited;
	// A LinkPass sign-in return reboots mid-session and lands on an already-themed page;
	// the five-second hold that guards the first entry's theme flash would just be five
	// seconds of spinner there. Latched once per boot: the provider strips the auth params
	// mid-flight, and flipping the delay then would restart the cover's countdown.
	const loadingCoverDelayMs = useRef(isLinkPayAuthReturning() ? 1200 : 5000).current;
	// Use cookie-persisted locationId (same rationale as qrsId): URL params disappear after SPA refresh.
	const uuid = appLocationId ?? "";
	const {
		isLoading: qrSessionLoading,
		error: qrSessionError,
		status: qrSessionStatus,
	} = useCheckQrSession(uuid, qrsId ?? "");

	if (qrsId && qrSessionLoading) return null;
	if (qrsId && (qrSessionError || qrSessionStatus !== QR_SESSION_STATUS.OPEN)) {
		return <QrSessionExpiredPage />;
	}

	return (
		<ConditionTheme currentTheme={stallMode}>
			<Suspense fallback={<div>Loading...</div>}>
				<Routes>
					<Route path={PATH.SPLASH} element={<SplashScreenWrapper />} />
					<Route path={PATH.QR_SESSION_EXPIRED} element={<QrSessionExpiredPage />} />
					<Route
						path="*"
						element={
							<ConditionDelayClose check={showLoadingCover} delayTime={loadingCoverDelayMs}>
								<Layout>
									<ToastContainer />
									<PageTransition>
										<Routes>
											<Route path={PATH.HOME} element={<Navigate to={PATH.SPLASH} replace />} />
											<Route path={PATH.INFO} element={<Info />} />
											<Route path={PATH.PRODUCT_LIST} element={<ProductListPage />} />
											<Route path={PATH.PRODUCT_DETAIL} element={<ProductDetailPage />} />
											<Route path={PATH.CART} element={<CartPage />} />
											<Route path={PATH.PAYMENT} element={<PaymentPage />} />
											<Route path={PATH.ORDER_CONFIRMATION} element={<OrderConfirmation />} />
											<Route
												path={PATH.SEND_TO_KITCHEN_CONFIRM}
												element={<SendToKitchenConfirmPage />}
											/>
											<Route path={PATH.BILL} element={<KitchenOrderSentPage />} />
											<Route path={PATH.SHARE_CART} element={<ShareCartPage />} />
											<Route path="/404" element={<ErrorBoundaryPage />} />
										</Routes>
									</PageTransition>
								</Layout>
							</ConditionDelayClose>
						}
					/>
				</Routes>
			</Suspense>
		</ConditionTheme>
	);
}

function App() {
	// BrowserRouter needs basename to root the app correctly on server-side routing.
	// HashRouter (Cloud Storage static deploy) does not: hash routing ignores the pathname,
	// and adding basename causes React Router to reject URLs like /uuid=xxx that don't
	// start with the basename prefix.
	const basename = isRootPath || isMultiStallPath ? publicBaseUrl : undefined;

	return (
		<FinalRouter basename={basename}>
			<AppRoutes />
		</FinalRouter>
	);
}

export default App;
